import AppActionCreators from '../action-creators/AppActionCreators'
import type { Subprofile } from '../stores/types'

type ProfileSlotData = Record<
  string,
  Array<{ timestamp: number; isSlotFree: boolean }>
>
import API from './API'

type RawSubprofile = {
  profile_id: string
  id: string
  service_id: string
  avatar: string
  name: string
  is_shared: boolean
}

type CreateSubprofileResponse = {
  success?: boolean
  subprofile: RawSubprofile
}

type FetchSubprofilesResponse = {
  subprofiles: RawSubprofile[]
}

class APIRequestError extends Error {
  name = 'APIRequestError'
}

const WebAPIUtils = {
  createNewSubprofile: (
    profileId: string,
    data: { name: string },
  ): Promise<Subprofile> => {
    AppActionCreators.createSubprofilePending(profileId)

    return API.post<CreateSubprofileResponse>(
      `profiles/${profileId}/subprofiles/create.json`,
      data,
    )
      .catch(() => {
        throw new APIRequestError(`Whoops, Buffer's servers couldn't be reached
          to save the board, sorry about that. Would you be up for trying again?`)
      })
      .then((response) => {
        if (
          !Object.prototype.hasOwnProperty.call(response, 'success') ||
          response.success === false
        ) {
          throw new APIRequestError(`The board couldn't be saved, sorry about that.
            Would you be up for trying again?`)
        } else {
          return {
            profileId: response.subprofile.profile_id,
            id: response.subprofile.id,
            serviceId: response.subprofile.service_id,
            avatar: response.subprofile.avatar,
            name: response.subprofile.name,
            isShared: response.subprofile.is_shared,
          }
        }
      })
  },

  fetchProfileSubprofiles: (
    profileId: string,
  ): Promise<{ subprofiles: Subprofile[] }> =>
    API.get<FetchSubprofilesResponse>(
      `profiles/${profileId}/fetch_subprofiles.json`,
    ).then((response) => ({
      subprofiles: response.subprofiles.map(
        // eslint-disable-next-line camelcase
        ({ profile_id, id, service_id, avatar, name, is_shared }) => ({
          profileId: profile_id,
          isShared: is_shared,
          id,
          serviceId: service_id,
          avatar,
          name,
        }),
      ),
    })),

  getProfileSlotDataForDateRange: (
    profileId: string,
    startDay: string | number,
    endDay: string | number,
  ): Promise<ProfileSlotData> =>
    API.get<ProfileSlotData>(`profiles/${profileId}/schedules/slots.json`, {
      start_day: startDay,
      end_day: endDay,
    }),

  getFacebookDomainOwnershipForProfile: (
    profileId: string,
    url: string,
  ): Promise<unknown> =>
    API.get(`profiles/${profileId}/facebook_domain_ownership.json`, {
      profile_id: profileId,
      url,
    }),
}

export default WebAPIUtils
